import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain,
  Lightbulb,
  TrendingUp,
  Shield,
  Mic,
  Target,
  Zap,
  ArrowRight,
  Download,
  RefreshCw,
  Star,
  Award,
  Building2,
  Users,
  ChevronDown,
  Trophy,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  Eye,
  ChevronRight
} from 'lucide-react';

interface GeneratedIdea {
  id: string;
  title: string;
  description: string;
  category: string;
  confidence: number;
  impact: 'High' | 'Medium' | 'Low';
  effort: 'High' | 'Medium' | 'Low';
  trizPrinciple: string;
  marketPotential: number;
  ranking: number;
  trizFlow: {
    contradiction: string;
    principle: string;
    implementation: string;
    benefit: string;
  };
  marketResearch: {
    marketSize: string;
    competitorAnalysis: string;
    targetSegment: string;
    revenueModel: string;
    timeToMarket: string;
    riskFactors: string[];
  };
  keyPoints: string[];
  detailedAnalysis: string;
}

const AITrizIdeation = () => {
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('FinTech');
  const [challenges, setChallenges] = useState('');
  const [targetMarket, setTargetMarket] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedIdeas, setGeneratedIdeas] = useState<GeneratedIdea[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [expandedIdea, setExpandedIdea] = useState<string | null>(null);

  const industries = [
    'FinTech', 'HealthTech', 'EdTech', 'E-commerce', 'SaaS', 'IoT', 'AI/ML', 'Blockchain',
    'Gaming', 'Manufacturing', 'Energy', 'Transportation', 'Food & Beverage', 'Real Estate'
  ];

  const stats = {
    ideasGenerated: 2847,
    successRate: 94,
    patentsFiled: 156
  };

  const handleGenerateIdeas = async () => {
    setIsGenerating(true);
    
    // Simulate AI processing
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    const ideas: GeneratedIdea[] = [
      {
        id: '1',
        title: 'Blockchain-based Identity Verification',
        description: 'TRIZ contradiction analysis suggests implementing decentralized identity verification to solve privacy vs. security paradox.',
        category: 'Security Innovation',
        confidence: 92,
        impact: 'High',
        effort: 'Medium',
        trizPrinciple: 'Principle 15: Dynamics - Dynamic identity verification',
        marketPotential: 8.5,
        ranking: 1,
        trizFlow: {
          contradiction: 'Privacy vs Security: Users want privacy but institutions need security verification',
          principle: 'Principle 15: Dynamics - Make objects adaptive and flexible',
          implementation: 'Dynamic blockchain verification that reveals only necessary data per transaction',
          benefit: 'Solves fundamental tension between user privacy and institutional security requirements'
        },
        marketResearch: {
          marketSize: '$24.3B global identity verification market by 2027',
          competitorAnalysis: 'Limited blockchain-based solutions; traditional KYC vendors lack privacy focus',
          targetSegment: 'Financial institutions, fintech startups, and privacy-conscious users',
          revenueModel: 'SaaS subscription + transaction fees (estimated $50-200 per verification)',
          timeToMarket: '18-24 months for MVP, 36 months for full platform',
          riskFactors: ['Regulatory uncertainty', 'Blockchain scalability', 'User adoption curve']
        },
        keyPoints: [
          'Addresses $500B+ fraud loss annually in financial services',
          'GDPR/CCPA compliant by design',
          'Reduces KYC costs by 60-80% through automation',
          'First-mover advantage in privacy-preserving identity verification'
        ],
        detailedAnalysis: 'This solution ranks #1 due to its direct application of TRIZ Principle 15 (Dynamics) to solve a critical market contradiction. The blockchain approach transforms static identity verification into a dynamic, privacy-preserving system. Market timing is optimal with increasing privacy regulations and rising fraud costs.'
      },
      {
        id: '2',
        title: 'Predictive Fraud Detection Engine',
        description: 'Machine learning model to predict fraudulent transactions using behavioral pattern analysis.',
        category: 'AI/ML Feature',
        confidence: 87,
        impact: 'High',
        effort: 'High',
        trizPrinciple: 'Principle 24: Intermediary - AI as detection intermediary',
        marketPotential: 9.2,
        ranking: 2,
        trizFlow: {
          contradiction: 'Speed vs Accuracy: Fast transactions needed but thorough verification takes time',
          principle: 'Principle 24: Intermediary - Use an intermediary to carry out actions',
          implementation: 'AI intermediary analyzes patterns in real-time without slowing transactions',
          benefit: 'Achieves both speed and accuracy through intelligent intermediation'
        },
        marketResearch: {
          marketSize: '$38.2B fraud detection market, growing 15.8% CAGR',
          competitorAnalysis: 'Established players (SAS, FICO) focus on rules-based systems',
          targetSegment: 'Banks, payment processors, e-commerce platforms',
          revenueModel: 'Tiered SaaS: $10K-500K annually based on transaction volume',
          timeToMarket: '12-18 months for core engine, 24 months for advanced features',
          riskFactors: ['Model accuracy requirements', 'Data privacy regulations', 'False positive rates']
        },
        keyPoints: [
          'Reduces fraud losses by 40-60% versus traditional rule-based systems',
          'Real-time processing capability (sub-100ms response time)',
          'Adaptive learning from new fraud patterns',
          'Integration with existing banking infrastructure'
        ],
        detailedAnalysis: 'Ranked #2 for its perfect application of TRIZ Principle 24. The AI intermediary elegantly solves the speed-accuracy contradiction in fraud detection. High market potential driven by increasing digital payments and sophisticated fraud techniques.'
      },
      {
        id: '3',
        title: 'Voice-Activated Financial Assistant',
        description: 'Natural language processing for voice-controlled banking operations and financial advice.',
        category: 'UX Innovation',
        confidence: 78,
        impact: 'Medium',
        effort: 'Medium',
        trizPrinciple: 'Principle 17: Another Dimension - Voice interface dimension',
        marketPotential: 7.8,
        ranking: 5,
        trizFlow: {
          contradiction: 'Complexity vs Simplicity: Financial operations are complex but users want simplicity',
          principle: 'Principle 17: Another Dimension - Move from 2D to 3D, add voice dimension',
          implementation: 'Voice interface adds natural communication layer over complex financial systems',
          benefit: 'Maintains system complexity while providing intuitive user experience'
        },
        marketResearch: {
          marketSize: '$11.9B voice assistant market in finance by 2025',
          competitorAnalysis: 'Early stage: Bank of America\'s Erica, JPMorgan\'s Amy, limited features',
          targetSegment: 'Digital natives, accessibility users, busy professionals',
          revenueModel: 'White-label licensing to banks: $50K-2M setup + $5-50 per user/month',
          timeToMarket: '15-20 months for basic version, 30 months for advanced AI',
          riskFactors: ['Voice recognition accuracy', 'Security concerns', 'Regulatory compliance']
        },
        keyPoints: [
          'Addresses accessibility needs for visually impaired users',
          'Reduces customer service costs by 30-50%',
          'Increases user engagement and daily banking interactions',
          'Natural language understanding for complex financial queries'
        ],
        detailedAnalysis: 'Ranked #5 due to moderate market potential and technical challenges. TRIZ Principle 17 application is solid - adding voice dimension to solve complexity-simplicity contradiction. Market is emerging but competitive landscape is developing rapidly.'
      },
      {
        id: '4',
        title: 'Micro-Investment Social Platform',
        description: 'Social trading platform enabling micro-investments with gamification elements.',
        category: 'Platform Innovation',
        confidence: 85,
        impact: 'High',
        effort: 'Medium',
        trizPrinciple: 'Principle 40: Composite Structures - Social + Financial',
        marketPotential: 8.7,
        ranking: 3,
        trizFlow: {
          contradiction: 'Professional vs Accessible: Investment requires expertise but should be accessible to all',
          principle: 'Principle 40: Composite Structures - Combine different elements',
          implementation: 'Combines social learning with micro-investment mechanics',
          benefit: 'Makes professional investment knowledge accessible through social learning'
        },
        marketResearch: {
          marketSize: '$29.8B social trading market by 2026, 8.2% CAGR',
          competitorAnalysis: 'Robinhood, eToro focus on trading; lacking social learning aspects',
          targetSegment: 'Gen Z/Millennials, beginner investors, social media natives',
          revenueModel: 'Freemium: Free basic + $9.99/month premium + transaction fees',
          timeToMarket: '12-16 months for MVP, 24 months for full social features',
          riskFactors: ['Regulatory compliance', 'Market volatility impact', 'User acquisition costs']
        },
        keyPoints: [
          'Targets $68T wealth transfer to younger generations',
          'Gamification increases user engagement by 300-500%',
          'Social proof reduces investment anxiety for beginners',
          'Viral growth potential through social sharing'
        ],
        detailedAnalysis: 'Ranked #3 for strong TRIZ application using Principle 40 to combine social and financial elements. High market potential with demographic trends favoring social investment platforms. Good balance of innovation and feasibility.'
      },
      {
        id: '5',
        title: 'Real-time Risk Assessment API',
        description: 'Dynamic risk evaluation API that adapts to market conditions in real-time.',
        category: 'Infrastructure',
        confidence: 89,
        impact: 'Medium',
        effort: 'High',
        trizPrinciple: 'Principle 19: Periodic Action - Continuous risk monitoring',
        marketPotential: 8.1,
        ranking: 4,
        trizFlow: {
          contradiction: 'Static vs Dynamic: Risk models need stability but markets are constantly changing',
          principle: 'Principle 19: Periodic Action - Replace continuous action with periodic pulses',
          implementation: 'Continuous micro-adjustments to risk models based on real-time data',
          benefit: 'Maintains model stability while adapting to dynamic market conditions'
        },
        marketResearch: {
          marketSize: '$45.1B risk management market, 14.1% CAGR through 2028',
          competitorAnalysis: 'Legacy providers (Moody\'s, S&P) slow to adopt real-time capabilities',
          targetSegment: 'Investment banks, hedge funds, fintech lenders, insurance companies',
          revenueModel: 'Enterprise API pricing: $25K-1M annually based on API calls',
          timeToMarket: '18-30 months for robust API, 36 months for advanced ML models',
          riskFactors: ['Data quality requirements', 'Model interpretability', 'Market stress testing']
        },
        keyPoints: [
          'Reduces portfolio risk by 20-35% through real-time adjustments',
          'Enables new financial products with dynamic pricing',
          'Meets increasing regulatory requirements for stress testing',
          'Scalable infrastructure for high-frequency trading'
        ],
        detailedAnalysis: 'Ranked #4 for excellent TRIZ Principle 19 application solving static-dynamic contradiction in risk management. Strong technical innovation but requires significant development effort. Enterprise market provides high-value but longer sales cycles.'
      }
    ];
    
    // Sort ideas by ranking before setting them
    const sortedIdeas = ideas.sort((a, b) => a.ranking - b.ranking);
    setGeneratedIdeas(sortedIdeas);
    setIsGenerating(false);
    setShowResults(true);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Security Innovation': return Shield;
      case 'AI/ML Feature': return Brain;
      case 'UX Innovation': return Mic;
      case 'Platform Innovation': return Users;
      case 'Infrastructure': return Building2;
      default: return Lightbulb;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Security Innovation': return 'from-red-500 to-orange-500';
      case 'AI/ML Feature': return 'from-purple-500 to-blue-500';
      case 'UX Innovation': return 'from-green-500 to-teal-500';
      case 'Platform Innovation': return 'from-indigo-500 to-purple-500';
      case 'Infrastructure': return 'from-gray-500 to-slate-500';
      default: return 'from-blue-500 to-indigo-500';
    }
  };

  return (
    <div className="h-full bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-7xl mx-auto h-full">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl flex items-center justify-center">
                  <Brain className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold gradient-text">AI TRIZ Product Ideas</h1>
                  <p className="text-slate-600 dark:text-slate-400">
                    AI Innovation Engine - Powered by TRIZ methodology and advanced ML algorithms
                  </p>
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">Innovation Score</div>
              <div className="text-2xl font-bold gradient-text">JD</div>
              <div className="text-sm text-slate-500">John Doe</div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl p-6 border border-slate-200/50 dark:border-slate-700/50"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-blue-600">{stats.ideasGenerated.toLocaleString()}</div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">Ideas Generated</div>
                </div>
                <Lightbulb className="w-8 h-8 text-blue-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl p-6 border border-slate-200/50 dark:border-slate-700/50"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-green-600">{stats.successRate}%</div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">Success Rate</div>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl p-6 border border-slate-200/50 dark:border-slate-700/50"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-purple-600">{stats.patentsFiled}</div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">Patents Filed</div>
                </div>
                <Award className="w-8 h-8 text-purple-500" />
              </div>
            </motion.div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full">
          {/* Input Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl p-8 border border-slate-200/50 dark:border-slate-700/50"
          >
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-6">
              Company Research Input
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Enter company name"
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Industry
                </label>
                <div className="relative">
                  <select
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                  >
                    {industries.map((ind) => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Current Challenges
                </label>
                <textarea
                  value={challenges}
                  onChange={(e) => setChallenges(e.target.value)}
                  placeholder="Describe current business challenges, market gaps, or technical limitations..."
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Target Market
                </label>
                <textarea
                  value={targetMarket}
                  onChange={(e) => setTargetMarket(e.target.value)}
                  placeholder="Define your target audience and market segment"
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleGenerateIdeas}
                disabled={isGenerating || !companyName.trim()}
                className="w-full btn-primary flex items-center justify-center space-x-3 py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    <span>Generating AI Roadmap...</span>
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    <span>Generate AI Roadmap</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>

          {/* Results Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl p-8 border border-slate-200/50 dark:border-slate-700/50"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                AI-Generated Ideas
              </h2>
              {showResults && (
                <div className="flex space-x-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-2 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/70"
                  >
                    <Download className="w-4 h-4" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowResults(false)}
                    className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </motion.button>
                </div>
              )}
            </div>

            <div className="h-full overflow-y-auto">
              <AnimatePresence>
                {showResults ? (
                  <div className="space-y-6">
                    {/* Top Ideas Ranking */}
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-4 border border-yellow-200/50 dark:border-yellow-700/50">
                      <div className="flex items-center space-x-2 mb-3">
                        <Trophy className="w-5 h-5 text-yellow-600" />
                        <h3 className="font-bold text-slate-900 dark:text-slate-100">Top 5 AI-Generated Ideas</h3>
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">
                        Ranked by TRIZ effectiveness, market potential, and implementation feasibility
                      </div>
                    </div>

                    {generatedIdeas.map((idea, index) => {
                        const Icon = getCategoryIcon(idea.category);
                        const isExpanded = expandedIdea === idea.id;
                        
                        return (
                          <motion.div
                            key={idea.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="bg-gradient-to-r from-white to-slate-50 dark:from-slate-700 dark:to-slate-600 rounded-lg border border-slate-200/50 dark:border-slate-600/50 overflow-hidden"
                          >
                            {/* Main Idea Card */}
                            <div className="p-6">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center space-x-3">
                                  <div className="relative">
                                    <div className={`w-10 h-10 bg-gradient-to-r ${getCategoryColor(idea.category)} rounded-lg flex items-center justify-center`}>
                                      <Icon className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
                                      <span className="text-xs font-bold text-white">#{idea.ranking}</span>
                                    </div>
                                  </div>
                                  <div>
                                    <h3 className="font-bold text-slate-900 dark:text-slate-100">{idea.title}</h3>
                                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">{idea.category}</span>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <div className="flex items-center space-x-1">
                                    <Star className="w-4 h-4 text-yellow-500" />
                                    <span className="text-sm font-bold text-slate-900 dark:text-slate-100">{idea.confidence}%</span>
                                  </div>
                                  <button
                                    onClick={() => setExpandedIdea(isExpanded ? null : idea.id)}
                                    className="p-1 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                                  >
                                    <ChevronRight className={`w-4 h-4 text-slate-400 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                  </button>
                                </div>
                              </div>

                              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">{idea.description}</p>

                              <div className="flex items-center justify-between mb-4">
                                <div className="flex space-x-4">
                                  <div className="flex items-center space-x-1">
                                    <Target className="w-4 h-4 text-red-500" />
                                    <span className="text-xs font-medium">Impact: <span className="text-slate-900 dark:text-slate-100">{idea.impact}</span></span>
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <Zap className="w-4 h-4 text-orange-500" />
                                    <span className="text-xs font-medium">Effort: <span className="text-slate-900 dark:text-slate-100">{idea.effort}</span></span>
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <DollarSign className="w-4 h-4 text-green-500" />
                                    <span className="text-xs font-medium">Market: <span className="text-slate-900 dark:text-slate-100">{idea.marketPotential}/10</span></span>
                                  </div>
                                </div>
                              </div>

                              {/* Key Points Preview */}
                              <div className="flex flex-wrap gap-2">
                                {idea.keyPoints.slice(0, 2).map((point, idx) => (
                                  <span key={idx} className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full">
                                    {point.length > 40 ? point.substring(0, 40) + '...' : point}
                                  </span>
                                ))}
                                {idea.keyPoints.length > 2 && (
                                  <span className="text-xs text-slate-500">+{idea.keyPoints.length - 2} more</span>
                                )}
                              </div>
                            </div>

                            {/* Expanded Details */}
                            <AnimatePresence>
                              {isExpanded && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="border-t border-slate-200/50 dark:border-slate-600/50"
                                >
                                  <div className="p-6 space-y-6">
                                    {/* TRIZ Flow Analysis */}
                                    <div>
                                      <h4 className="font-bold text-slate-900 dark:text-slate-100 mb-3 flex items-center space-x-2">
                                        <Brain className="w-4 h-4 text-purple-600" />
                                        <span>TRIZ Flow Analysis</span>
                                      </h4>
                                      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 space-y-3">
                                        <div>
                                          <span className="text-xs font-medium text-purple-700 dark:text-purple-300">Contradiction:</span>
                                          <p className="text-sm text-slate-700 dark:text-slate-300">{idea.trizFlow.contradiction}</p>
                                        </div>
                                        <div>
                                          <span className="text-xs font-medium text-purple-700 dark:text-purple-300">TRIZ Principle:</span>
                                          <p className="text-sm text-slate-700 dark:text-slate-300">{idea.trizFlow.principle}</p>
                                        </div>
                                        <div>
                                          <span className="text-xs font-medium text-purple-700 dark:text-purple-300">Implementation:</span>
                                          <p className="text-sm text-slate-700 dark:text-slate-300">{idea.trizFlow.implementation}</p>
                                        </div>
                                        <div>
                                          <span className="text-xs font-medium text-purple-700 dark:text-purple-300">Benefit:</span>
                                          <p className="text-sm text-slate-700 dark:text-slate-300">{idea.trizFlow.benefit}</p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Market Research */}
                                    <div>
                                      <h4 className="font-bold text-slate-900 dark:text-slate-100 mb-3 flex items-center space-x-2">
                                        <BarChart3 className="w-4 h-4 text-green-600" />
                                        <span>Market Research & Potential</span>
                                      </h4>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                                          <div className="flex items-center space-x-2 mb-2">
                                            <DollarSign className="w-4 h-4 text-green-600" />
                                            <span className="text-xs font-medium text-green-700 dark:text-green-300">Market Size</span>
                                          </div>
                                          <p className="text-sm text-slate-700 dark:text-slate-300">{idea.marketResearch.marketSize}</p>
                                        </div>
                                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                                          <div className="flex items-center space-x-2 mb-2">
                                            <Users className="w-4 h-4 text-blue-600" />
                                            <span className="text-xs font-medium text-blue-700 dark:text-blue-300">Target Segment</span>
                                          </div>
                                          <p className="text-sm text-slate-700 dark:text-slate-300">{idea.marketResearch.targetSegment}</p>
                                        </div>
                                        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                                          <div className="flex items-center space-x-2 mb-2">
                                            <Eye className="w-4 h-4 text-purple-600" />
                                            <span className="text-xs font-medium text-purple-700 dark:text-purple-300">Competition</span>
                                          </div>
                                          <p className="text-sm text-slate-700 dark:text-slate-300">{idea.marketResearch.competitorAnalysis}</p>
                                        </div>
                                        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                                          <div className="flex items-center space-x-2 mb-2">
                                            <Clock className="w-4 h-4 text-orange-600" />
                                            <span className="text-xs font-medium text-orange-700 dark:text-orange-300">Time to Market</span>
                                          </div>
                                          <p className="text-sm text-slate-700 dark:text-slate-300">{idea.marketResearch.timeToMarket}</p>
                                        </div>
                                      </div>
                                      
                                      <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                                        <div className="flex items-center space-x-2 mb-2">
                                          <DollarSign className="w-4 h-4 text-yellow-600" />
                                          <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300">Revenue Model</span>
                                        </div>
                                        <p className="text-sm text-slate-700 dark:text-slate-300">{idea.marketResearch.revenueModel}</p>
                                      </div>
                                    </div>

                                    {/* Key Points */}
                                    <div>
                                      <h4 className="font-bold text-slate-900 dark:text-slate-100 mb-3 flex items-center space-x-2">
                                        <CheckCircle className="w-4 h-4 text-blue-600" />
                                        <span>Key Value Points</span>
                                      </h4>
                                      <div className="space-y-2">
                                        {idea.keyPoints.map((point, idx) => (
                                          <div key={idx} className="flex items-start space-x-2">
                                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                            <span className="text-sm text-slate-700 dark:text-slate-300">{point}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Risk Factors */}
                                    <div>
                                      <h4 className="font-bold text-slate-900 dark:text-slate-100 mb-3 flex items-center space-x-2">
                                        <AlertTriangle className="w-4 h-4 text-red-600" />
                                        <span>Risk Factors</span>
                                      </h4>
                                      <div className="space-y-2">
                                        {idea.marketResearch.riskFactors.map((risk, idx) => (
                                          <div key={idx} className="flex items-start space-x-2">
                                            <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                                            <span className="text-sm text-slate-700 dark:text-slate-300">{risk}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Detailed Analysis */}
                                    <div>
                                      <h4 className="font-bold text-slate-900 dark:text-slate-100 mb-3 flex items-center space-x-2">
                                        <Award className="w-4 h-4 text-purple-600" />
                                        <span>Why This Ranks #{idea.ranking}</span>
                                      </h4>
                                      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
                                        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{idea.detailedAnalysis}</p>
                                      </div>
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        );
                      })}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                      <Brain className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-500 dark:text-slate-400">
                        Enter your company details and generate AI-powered product ideas
                      </p>
                    </div>
                  </div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AITrizIdeation; 